import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const ProductsPage = () => {
  const products = [
    {
      name: 'AI Content Studio',
      desc: 'Complete AI-powered content creation platform for brands.',
      status: 'Live',
      link: 'https://fusionscope.in',
    },
    {
      name: 'Brand Voice AI',
      desc: 'AI tool that maintains consistent brand voice across all content.',
      status: 'Coming Soon',
      link: null,
    },
    {
      name: 'Visual AI Pro',
      desc: 'Professional AI product photography and rendering platform.',
      status: 'Beta',
      link: null,
    },
    {
      name: 'Campaign Builder',
      desc: 'End-to-end marketing campaign management SaaS.',
      status: 'Coming Soon',
      link: null,
    },
    {
      name: 'Character Studio',
      desc: 'AI character design and animation tool for brands.',
      status: 'Coming Soon',
      link: null,
    },
    {
      name: 'Video AI Suite',
      desc: 'Automated video production and editing platform.',
      status: 'In Development',
      link: null,
    },
  ];

  return (
    <div data-testid="products-page" className="min-h-screen">
      {/* Hero with Video Background */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }}
          >
            <source
              src="https://test-videos.co.uk/vids/jellyfish/mp4/h264/1080/Jellyfish_1080_10s_5MB.mp4"
              type="video/mp4"
            />
          </video>
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.2) 0%, rgba(10, 10, 10, 0) 70%)',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none uppercase mb-6 text-white drop-shadow-2xl">
              What We've
              <br />
              <span className="text-purple-400">Built</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl drop-shadow-lg">
              Tools and platforms that transform how brands create content.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                data-testid={`product-card-${index}`}
                className="bg-card border border-themed rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute top-4 right-4">
                  <span
                    className={`text-xs font-bold px-3 py-1 rounded-full ${
                      product.status === 'Live'
                        ? 'bg-green-500/20 text-green-400'
                        : product.status === 'Beta'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}
                  >
                    {product.status}
                  </span>
                </div>

                <div className="relative z-10 mt-8">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center text-purple-500 mb-6">
                    <div className="text-2xl font-black">{product.name.charAt(0)}</div>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{product.name}</h3>
                  <p className="text-muted mb-6 leading-relaxed">{product.desc}</p>
                  {product.link ? (
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-500 font-bold hover:gap-3 transition-all"
                    >
                      Visit Site <ExternalLink size={16} />
                    </a>
                  ) : (
                    <div className="text-muted text-sm">More details coming soon</div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-extrabold text-primary tracking-tight leading-tight mb-8">
              Have an idea for a product?
              <br />
              <span className="text-purple-500">Let's build it together.</span>
            </h2>
            <a
              href="/contact"
              data-testid="products-contact-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-5 rounded-full font-bold text-lg tracking-wide transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(124,58,237,0.6)] inline-block"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;